import { getLang, intlLocale, t, type Lang } from "@/lib/i18n";
import { plural } from "@/lib/i18n/plural";

export { plural };

/**
 * Formatting always in the studio's time zone, never the device's, and in the
 * visitor's interface language. Day and month names are our own tables, not
 * Intl's: browsers without ru-KZ / kk data would otherwise fall back to English.
 */

const NAMES: Record<Lang, { weekdayShort: string[]; weekdayLong: string[]; month: string[]; monthShort: string[] }> = {
  ru: {
    weekdayShort: ["вс", "пн", "вт", "ср", "чт", "пт", "сб"],
    weekdayLong: ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"],
    month: ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],
    monthShort: ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"],
  },
  kk: {
    weekdayShort: ["жс", "дс", "сс", "ср", "бс", "жм", "сн"],
    weekdayLong: ["жексенбі", "дүйсенбі", "сейсенбі", "сәрсенбі", "бейсенбі", "жұма", "сенбі"],
    month: ["қаңтар", "ақпан", "наурыз", "сәуір", "мамыр", "маусым", "шілде", "тамыз", "қыркүйек", "қазан", "қараша", "желтоқсан"],
    monthShort: ["қаң", "ақп", "нау", "сәу", "мам", "мау", "шіл", "там", "қыр", "қаз", "қар", "жел"],
  },
};

/** Monday-first short weekday names (1 = Monday … 7 = Sunday), for opening hours. */
export function weekdayShortIso(isoWeekday: number): string {
  const name = NAMES[getLang()].weekdayShort[isoWeekday % 7]!;
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** Consecutive weekdays (1 = Mon … 7 = Sun) with the same value merged: one line «Пн–Вс» when every day is alike. */
export function groupWeekdays(valueOf: (isoWeekday: number) => string): { from: number; to: number; value: string }[] {
  const groups: { from: number; to: number; value: string }[] = [];
  for (let d = 1; d <= 7; d++) {
    const value = valueOf(d);
    const last = groups.at(-1);
    if (last && last.value === value) last.to = d;
    else groups.push({ from: d, to: d, value });
  }
  return groups;
}

export function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat(intlLocale(), { style: "currency", currency, currencyDisplay: "narrowSymbol", maximumFractionDigits: 0 }).format(amount);
}

export function formatPrice(s: { price_amount: number; price_is_from: boolean; price_on_request?: boolean }, currency: string) {
  if (s.price_on_request) return t().price.onRequest;
  const v = formatMoney(s.price_amount, currency);
  return s.price_is_from ? t().price.from(v) : v;
}

export function formatDuration(minutes: number): string {
  const d = t().duration;
  if (minutes >= 1440) {
    const days = Math.round((minutes / 1440) * 10) / 10;
    return d.days(days.toString().replace(".", ","), Math.ceil(days));
  }
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return d.minutes(m);
  return m ? d.hoursMinutes(h, m) : d.hours(h);
}

function dateParts(isoDate: string) {
  const [y, m, d] = isoDate.split("-").map(Number);
  return { day: d!, month: m! - 1, weekday: new Date(Date.UTC(y!, m! - 1, d!, 12)).getUTCDay() };
}

/** «сб, 17 октября, 20:00» / «17 қазан, сн, 20:00». */
export function formatDateTime(iso: string, tz: string): string {
  const lang = getLang();
  const n = NAMES[lang];
  const p = dateParts(localDateOf(iso, tz));
  const time = formatTime(iso, tz);
  return lang === "kk"
    ? `${p.day} ${n.month[p.month]}, ${n.weekdayShort[p.weekday]}, ${time}`
    : `${n.weekdayShort[p.weekday]}, ${p.day} ${n.month[p.month]}, ${time}`;
}

export function formatTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
}

export function formatDay(isoDate: string): { weekday: string; day: string; month: string } {
  const n = NAMES[getLang()];
  const p = dateParts(isoDate);
  return { weekday: n.weekdayShort[p.weekday]!, day: String(p.day), month: n.monthShort[p.month]! };
}

/** «суббота, 17 октября» / «17 қазан, сенбі». */
export function formatLongDay(isoDate: string): string {
  const lang = getLang();
  const n = NAMES[lang];
  const p = dateParts(isoDate);
  return lang === "kk" ? `${p.day} ${n.month[p.month]}, ${n.weekdayLong[p.weekday]}` : `${n.weekdayLong[p.weekday]}, ${p.day} ${n.month[p.month]}`;
}

/** Today's date (YYYY-MM-DD) in a time zone. */
export function todayIn(tz: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

export function addDays(isoDate: string, n: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Wall-clock date+time in a zone → ISO instant (handles DST by iterating the offset). */
export function zonedToIso(date: string, time: string, tz: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  let guess = Date.UTC(y!, mo! - 1, d!, h!, mi!);
  for (let i = 0; i < 3; i++) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
        .formatToParts(new Date(guess))
        .map((p) => [p.type, p.value]),
    );
    const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute));
    const diff = asUtc - Date.UTC(y!, mo! - 1, d!, h!, mi!);
    if (diff === 0) break;
    guess -= diff;
  }
  return new Date(guess).toISOString();
}

export function localDateOf(iso: string, tz: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
}
